/**
 * "Competência" é o mês de referência de uma venda, meta ou fechamento,
 * no formato AAAA-MM (ex.: "2026-11").
 *
 * Regras de negócio aplicadas aqui:
 *  - A competência de uma venda vem da DATA DA VENDA no Conta Azul, que é
 *    uma data de calendário (AAAA-MM-DD). Por isso NÃO convertemos fuso:
 *    uma venda de 30/11 é sempre de novembro.
 *  - "Mês atual" e "hoje" dependem do fuso da agência (APP_TIMEZONE),
 *    nunca do fuso do servidor. Um servidor em UTC às 22h de 30/11 em
 *    Feira de Santana já estaria em 01/12.
 *  - O prazo para informar custos é o dia 5 do mês seguinte, inclusive.
 */

declare const competenciaBrand: unique symbol;

/** String AAAA-MM validada. O "brand" impede passar uma string qualquer. */
export type Competencia = string & { readonly [competenciaBrand]: true };

/** Data de calendário AAAA-MM-DD, sem horário nem fuso. */
export type CalendarDate = string;

const COMPETENCIA_REGEX = /^(\d{4})-(0[1-9]|1[0-2])$/;
const CALENDAR_DATE_REGEX = /^(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;

/**
 * Primeiro mês de operação do sistema. Vendas de meses anteriores não são
 * importadas nem aparecem em painéis, metas ou comissões.
 */
export const COMPETENCIA_INICIAL = '2026-11' as Competencia;

/** Mês dentro do período de operação do sistema? (AAAA-MM compara em ordem alfabética.) */
export function dentroDaOperacao(c: string): boolean {
  return c >= COMPETENCIA_INICIAL;
}

/** Dia do mês seguinte até o qual (inclusive) o vendedor pode informar custos. */
export const DIA_LIMITE_CUSTO_PADRAO = 5;

export function isCompetencia(value: unknown): value is Competencia {
  return typeof value === 'string' && COMPETENCIA_REGEX.test(value);
}

/** Converte e valida. Lança erro com mensagem clara se o formato for inválido. */
export function parseCompetencia(value: string): Competencia {
  if (!isCompetencia(value)) {
    throw new Error(`Competência inválida: "${value}". Use o formato AAAA-MM.`);
  }
  return value;
}

export function isCalendarDate(value: unknown): value is CalendarDate {
  if (typeof value !== 'string') return false;
  const match = CALENDAR_DATE_REGEX.exec(value);
  if (!match) return false;
  // Rejeita datas impossíveis como 2026-02-30.
  const [, y, m, d] = match;
  const date = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d)));
  return date.getUTCDate() === Number(d);
}

/** "2026-11-15" -> "2026-11". Também aceita ISO com horário ("2026-11-15T10:00:00Z"). */
export function competenciaFromCalendarDate(value: string): Competencia {
  const datePart = value.slice(0, 10);
  if (!isCalendarDate(datePart)) {
    throw new Error(`Data de venda inválida: "${value}". Use o formato AAAA-MM-DD.`);
  }
  return datePart.slice(0, 7) as Competencia;
}

/** Data de hoje (AAAA-MM-DD) no fuso informado. */
export function todayInTimeZone(now: Date, timeZone: string): CalendarDate {
  // en-CA formata como AAAA-MM-DD, independentemente da localidade do servidor.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);

  const get = (type: Intl.DateTimeFormatPartTypes): string => {
    const part = parts.find((p) => p.type === type);
    if (!part) throw new Error(`Falha ao formatar data no fuso ${timeZone}`);
    return part.value;
  };

  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Competência corrente no fuso da agência. */
export function currentCompetencia(now: Date, timeZone: string): Competencia {
  return todayInTimeZone(now, timeZone).slice(0, 7) as Competencia;
}

function splitCompetencia(c: Competencia): { year: number; month: number } {
  const [year, month] = c.split('-').map(Number);
  return { year: year as number, month: month as number };
}

function buildCompetencia(year: number, month: number): Competencia {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}` as Competencia;
}

export function nextCompetencia(c: Competencia): Competencia {
  const { year, month } = splitCompetencia(c);
  return month === 12 ? buildCompetencia(year + 1, 1) : buildCompetencia(year, month + 1);
}

export function previousCompetencia(c: Competencia): Competencia {
  const { year, month } = splitCompetencia(c);
  return month === 1 ? buildCompetencia(year - 1, 12) : buildCompetencia(year, month - 1);
}

/** Primeiro dia da competência: "2026-11" -> "2026-11-01". */
export function firstDayOf(c: Competencia): CalendarDate {
  return `${c}-01`;
}

/** Último dia da competência: "2026-02" -> "2026-02-28". */
export function lastDayOf(c: Competencia): CalendarDate {
  const { year, month } = splitCompetencia(c);
  // Dia 0 do mês seguinte = último dia do mês atual.
  const day = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${c}-${String(day).padStart(2, '0')}`;
}

/**
 * Último dia (inclusive) para informar custos de uma competência.
 * Ex.: competência "2026-11" -> "2026-12-05".
 */
export function prazoFinalCusto(c: Competencia, dia: number = DIA_LIMITE_CUSTO_PADRAO): CalendarDate {
  if (!Number.isInteger(dia) || dia < 1 || dia > 28) {
    // Limitamos a 28 para o prazo existir em todos os meses, inclusive fevereiro.
    throw new Error('O dia limite para informar custos deve estar entre 1 e 28.');
  }
  return `${nextCompetencia(c)}-${String(dia).padStart(2, '0')}`;
}

/** true se "hoje" (no fuso da agência) já passou do prazo de custos da competência. */
export function prazoCustoEncerrado(
  c: Competencia,
  hoje: CalendarDate,
  dia: number = DIA_LIMITE_CUSTO_PADRAO,
): boolean {
  // Datas AAAA-MM-DD podem ser comparadas como texto.
  return hoje > prazoFinalCusto(c, dia);
}
